// DFY content channel per company (DFY for hotels, proposal 2026-10-05, F-1): which n8n engine writes the
// week (profile), for which network (platform) and in which image format. The allowed values mirror the
// rules of the n8n workflow "DFY Hotel" (node "DFY Build Slots"): anything else falls back there too.
import { DFY_PROFILES, type DfyProfile } from "./pillarFormats.js";

export { DFY_PROFILES, type DfyProfile };

export const DFY_PLATFORMS = ["instagram", "facebook", "linkedin"] as const;
export type DfyPlatform = (typeof DFY_PLATFORMS)[number];

export const DFY_IMAGE_ASPECT_RATIOS = ["1:1", "4:5", "4:3", "3:4", "16:9", "9:16"] as const;
export type DfyImageAspectRatio = (typeof DFY_IMAGE_ASPECT_RATIOS)[number];

export function isDfyProfile(v: unknown): v is DfyProfile {
  return typeof v === "string" && (DFY_PROFILES as readonly string[]).includes(v);
}
export function isDfyPlatform(v: unknown): v is DfyPlatform {
  return typeof v === "string" && (DFY_PLATFORMS as readonly string[]).includes(v);
}
export function isDfyImageAspectRatio(v: unknown): v is DfyImageAspectRatio {
  return typeof v === "string" && (DFY_IMAGE_ASPECT_RATIOS as readonly string[]).includes(v);
}

/** Hotel defaults (Mike 2026-10-05): guests are on Instagram/Facebook, portrait 4:5. */
export const HOTEL_CHANNEL_DEFAULTS = { platform: "instagram", imageAspectRatio: "4:5" } as const;

/** The effective channel of a company: stored values, else the profile default. Agency keeps today's
 *  LinkedIn behaviour and sends no channel fields at all (its body stays byte-identical). */
export function resolveDfyChannel(company: {
  dfyProfile?: string | null; dfyPlatform?: string | null; dfyImageAspectRatio?: string | null;
}): { profile: DfyProfile; platform: DfyPlatform; imageAspectRatio: DfyImageAspectRatio } {
  const profile: DfyProfile = isDfyProfile(company.dfyProfile) ? company.dfyProfile : "agency";
  if (profile === "agency") {
    return { profile, platform: "linkedin", imageAspectRatio: "4:3" };
  }
  return {
    profile,
    platform: isDfyPlatform(company.dfyPlatform) ? company.dfyPlatform : HOTEL_CHANNEL_DEFAULTS.platform,
    imageAspectRatio: isDfyImageAspectRatio(company.dfyImageAspectRatio)
      ? company.dfyImageAspectRatio
      : HOTEL_CHANNEL_DEFAULTS.imageAspectRatio,
  };
}

/** Hotels get their strategy from the team (Hotel-Strategie in n8n, Signal Room), not from the app's
 *  strategy engine: until that engine knows hotels, the app saves the answers and mails us instead of
 *  calling n8n, and hides the reset buttons (Mike 2026-10-08). */
export function isStrategyByTeam(company: { dfyProfile?: string | null }): boolean {
  return resolveDfyChannel(company).profile === "hotel";
}
